import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { User, Lock, Mail, Phone, Heart, Building2, Sparkles, Star, Users, Fingerprint, ScanFace } from "lucide-react";
import { Button } from "@/components/ui/button";
import PhoneValidation from "../components/common/PhoneValidation";
import { AnimatedLogo, AnimatedText } from "../components/common/AnimatedLogo";
import { ParentIcon, TeacherIcon } from "../components/ui/AnimatedElements";
import useBiometricAuth from "../hooks/useBiometricAuth";

export default function LoginPage({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [userType, setUserType] = useState("parent");
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("access_ended")) {
      toast.error("Akses aplikasi Destiny Junior anda telah tamat kerana sesi anak anda telah berakhir. Terima kasih kerana menjadi sebahagian daripada keluarga Destiny Junior.", { duration: 12000 });
    }
  }, []);
  
  // Biometric auth hook
  const { 
    isSupported: biometricSupported, 
    isEnabled: biometricEnabled, 
    biometricType,
    authenticateWithBiometric,
    canUseBiometricLogin,
    isLoading: biometricHookLoading
  } = useBiometricAuth();
  
  const [parentData, setParentData] = useState({
    phone: "", password: "", name: "", spouse_name: "", email: "", branch: ""
  });
  
  const [staffData, setStaffData] = useState({
    email: "", password: "", name: "", role: "guru", branch: ""
  });
  
  const [forgotEmail, setForgotEmail] = useState("");
  const [phoneValidationPassed, setPhoneValidationPassed] = useState(true);

  // AUTO BIOMETRIC LOGIN - trigger Face ID/fingerprint automatically when page loads
  useEffect(() => {
    // Skip if already attempted, loading, or not ready
    if (autoLoginAttempted || biometricHookLoading || biometricLoading) {
      return;
    }
    
    // Check if biometric is available and enabled
    if (canUseBiometricLogin()) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(async () => {
        setAutoLoginAttempted(true);
        setBiometricLoading(true);
        
        try {
          const result = await authenticateWithBiometric();
          toast.success("Log masuk biometric berjaya! 🎉");
          onLogin(result.user, result.token);
        } catch (error) {
          // Silent fail for auto-login - user can still login manually
          console.log("Auto biometric login skipped:", error.message);
          // Only show toast if user actively cancelled
          if (error.name === 'NotAllowedError') {
            // User cancelled - don't show error, they can login manually
          } else {
            toast.info("Sila log masuk dengan kata laluan");
          }
        } finally {
          setBiometricLoading(false);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [canUseBiometricLogin, biometricHookLoading, autoLoginAttempted, authenticateWithBiometric, onLogin, biometricLoading]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axiosInstance.get('/public/branches');
        setBranches(response.data);
      } catch (error) {
        console.error("Error fetching branches");
      }
    };
    fetchBranches();
  }, []);

  const handleParentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/parent/login", {
        phone: parentData.phone, password: parentData.password
      });
      toast.success("Log masuk berjaya! 🎉");
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nombor telefon atau password tidak betul");
    } finally {
      setLoading(false);
    }
  };

  const handleParentRegister = async (e) => {
    e.preventDefault();
    if (parentData.password.length < 6) {
      toast.error("Password mestilah sekurang-kurangnya 6 aksara");
      return;
    }
    if (!parentData.branch) {
      toast.error("Sila pilih cawangan");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/parent/register", parentData);
      if (response.data.requires_verification) {
        toast.success("Pendaftaran berjaya! Sila semak email untuk pengesahan. 📧");
        setMode("login");
      } else if (response.data.requires_approval) {
        toast.success("Pendaftaran berjaya! Sila tunggu kelulusan daripada Admin. ⏳");
        setMode("login");
      } else {
        toast.success("Pendaftaran berjaya! 🎉");
        onLogin(response.data.user, response.data.token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat semasa pendaftaran");
    } finally {
      setLoading(false);
    }
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/auth/login", {
        email: staffData.email, password: staffData.password
      });
      toast.success("Log masuk berjaya! 🎉");
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email atau password tidak betul");
    } finally {
      setLoading(false);
    }
  };

  const handleStaffRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post("/auth/register", {
        email: staffData.email, password: staffData.password, name: staffData.name, role: staffData.role
      });
      toast.success("Pendaftaran berjaya! Sila tunggu kelulusan dari Superadmin. ✨");
      setMode("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat semasa pendaftaran");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Link reset password telah dihantar ke email anda. 📧");
      setMode("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat");
    } finally {
      setLoading(false);
    }
  };

  // Biometric login handler
  const handleBiometricLogin = async () => {
    if (!canUseBiometricLogin()) {
      toast.error("Log masuk biometric tidak tersedia");
      return;
    }
    
    setBiometricLoading(true);
    try {
      const result = await authenticateWithBiometric();
      toast.success("Log masuk biometric berjaya! 🎉");
      onLogin(result.user, result.token);
    } catch (error) {
      toast.error(error.message || "Pengesahan biometric gagal");
    } finally {
      setBiometricLoading(false);
    }
  };

  // Get biometric icon and name based on type
  // Android returns 'biometric' which covers both fingerprint and face
  const BiometricIcon = biometricType === 'face' ? ScanFace : Fingerprint;
  const getBiometricName = () => {
    if (biometricType === 'face') return 'Face ID';
    if (biometricType === 'biometric') return 'Biometrik'; // Android: fingerprint or face
    return 'Cap Jari';
  };

  // Floating decorations
  const decorations = [
    { emoji: '🎨', left: '5%', top: '15%', delay: 0 },
    { emoji: '🎪', left: '90%', top: '20%', delay: 0.5 },
    { emoji: '🎈', left: '8%', top: '75%', delay: 1 },
    { emoji: '🌟', left: '85%', top: '70%', delay: 1.5 },
    { emoji: '🎠', left: '15%', top: '45%', delay: 0.3 },
    { emoji: '🧸', left: '80%', top: '45%', delay: 0.8 },
    { emoji: '🎯', left: '25%', top: '85%', delay: 1.2 },
    { emoji: '🎭', left: '70%', top: '85%', delay: 0.6 },
  ];

  return (
    <div className="min-h-screen flex items-end justify-center p-4 pb-8 relative overflow-hidden">
      {/* Real Disneyland Castle Background - light blur */}
      <div 
        className="absolute inset-0 z-0"
        id="castle-bg"
        style={{
          backgroundImage: 'url(/bg-disneyland.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(1px)',
          transform: 'scale(1.01)',
        }}
      />
      {/* Mobile: push castle image up so more castle visible above form */}
      <style>{`@media (max-width: 640px) { #castle-bg { top: -30% !important; background-position: center top !important; } }`}</style>
      {/* Very light overlay for readability */}
      <div className="absolute inset-0 z-0" style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.12) 35%, rgba(255,255,255,0.3) 60%, rgba(255,255,255,0.5) 100%)'
      }} />
      
      {/* Colorful decorations */}
      {decorations.map((item, i) => (
        <div
          key={i}
          className="absolute text-4xl opacity-50 z-[1]"
          style={{
            left: item.left,
            top: item.top,
            animation: `floatDecor 4s ease-in-out infinite`,
            animationDelay: `${item.delay}s`,
          }}
        >
          {item.emoji}
        </div>
      ))}
      
      {/* Sun */}
      <div className="absolute top-8 right-8 opacity-60 z-[1]" style={{ animation: 'spin 30s linear infinite' }}>
        <svg width="60" height="60" viewBox="0 0 100 100">
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
            <rect key={i} x="46" y="5" width="8" height="18" rx="4" fill="#FFE066" transform={`rotate(${angle} 50 50)`} />
          ))}
          <circle cx="50" cy="50" r="25" fill="#FFE066" />
        </svg>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-4 relative">
            <div 
              className="absolute inset-0 w-40 h-40 rounded-full blur-2xl"
              style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE066 50%, #4DABF7 100%)', opacity: 0.4 }}
            />
            <AnimatedLogo 
              src="/logo-destiny.png" 
              alt="Destiny Junior Logo" 
              className="w-36 h-36 object-contain drop-shadow-2xl relative z-10"
              animationType="bounce"
            />
          </div>
          <h1 className="text-4xl font-black mb-1 whitespace-nowrap" style={{ fontFamily: "'Fredoka', 'Nunito', Arial, sans-serif", letterSpacing: '0.5px', textShadow: '0 1px 3px rgba(255,255,255,0.9), 0 0 8px rgba(255,255,255,0.7)' }}>
            <span style={{ color: '#FF6B6B' }}>DESTINY</span>{' '}
            <span style={{ color: '#FFB74D' }}>JUNIOR</span>
          </h1>
          <p className="text-gray-700 text-sm font-bold flex items-center justify-center gap-1" style={{ textShadow: '0 1px 4px rgba(255,255,255,0.9), 0 0 6px rgba(255,255,255,0.7)' }}>
            🎨 Belajar Sambil Bermain! 🎪
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="bg-white shadow-2xl p-6 relative overflow-hidden"
          style={{ 
            borderRadius: '32px',
            border: '4px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #FF6B6B, #FFA94D, #FFE066, #51CF66, #4DABF7, #BE4BDB)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          {/* Rainbow top bar */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400" style={{ borderRadius: '28px 28px 0 0' }} />
          
          {/* User Type Selector */}
          {mode === "login" && (
            <div className="flex gap-3 mb-5">
              <button
                onClick={() => setUserType("parent")}
                data-testid="login-tab-parent"
                className={`flex-1 py-3 px-4 rounded-2xl font-bold text-sm transition-all duration-300 ${
                  userType === "parent"
                    ? 'bg-gradient-to-r from-red-400 to-orange-400 text-white shadow-lg shadow-red-200/50 scale-105'
                    : 'bg-red-50 text-red-500 hover:bg-red-100'
                }`}
              >
                Ibu Bapa
              </button>
              <button
                onClick={() => setUserType("staff")}
                data-testid="login-tab-staff"
                className={`flex-1 py-3 px-4 rounded-2xl font-bold text-sm transition-all duration-300 ${
                  userType === "staff"
                    ? 'bg-gradient-to-r from-blue-400 to-purple-400 text-white shadow-lg shadow-blue-200/50 scale-105'
                    : 'bg-blue-50 text-blue-500 hover:bg-blue-100'
                }`}
              >
                Penyelia/Pendidik
              </button>
            </div>
          )}

          {/* PARENT LOGIN */}
          {mode === "login" && userType === "parent" && (
            <form onSubmit={handleParentLogin} className="space-y-4">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
                <ParentIcon size="lg" /> Log Masuk Ibu Bapa
              </h2>
              
              {/* Biometric Quick Login Button - Show if supported and enabled */}
              {biometricSupported && biometricEnabled && canUseBiometricLogin() && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    className="w-full py-4 bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white font-black text-lg rounded-2xl shadow-lg shadow-emerald-200/50 transition-all hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3"
                    data-testid="biometric-login-btn"
                  >
                    {biometricLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        Mengesahkan...
                      </span>
                    ) : (
                      <>
                        <BiometricIcon className="w-6 h-6" />
                        Log Masuk {getBiometricName()}
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 font-bold">ATAU</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                </div>
              )}
              
              {/* Biometric hint - Show if supported but NOT enabled */}
              {biometricSupported && !biometricEnabled && (
                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <BiometricIcon className="w-5 h-5" />
                    <span className="text-sm font-bold">
                      {getBiometricName()} tersedia!
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Log masuk dahulu, kemudian aktifkan di Tetapan Profil untuk log masuk pantas.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nombor Telefon</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
                  <input
                    type="tel"
                    data-testid="parent-phone-input"
                    className="w-full pl-12 pr-4 py-4 border-3 border-red-200 rounded-2xl focus:border-red-400 focus:ring-4 focus:ring-red-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={parentData.phone}
                    onChange={(e) => setParentData({...parentData, phone: e.target.value})}
                    required
                    placeholder="0123456789"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Kata Laluan</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
                  <input
                    type="password"
                    data-testid="parent-password-input"
                    className="w-full pl-12 pr-4 py-4 border-3 border-red-200 rounded-2xl focus:border-red-400 focus:ring-4 focus:ring-red-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={parentData.password}
                    onChange={(e) => setParentData({...parentData, password: e.target.value})}
                    required
                    placeholder="Masukkan kata laluan"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="parent-login-submit"
                disabled={loading} 
                className="w-full py-4 bg-gradient-to-r from-red-400 to-orange-400 hover:from-red-500 hover:to-orange-500 text-white font-black text-lg rounded-2xl shadow-lg shadow-red-200/50 transition-all hover:shadow-xl hover:-translate-y-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    Memproses...
                  </span>
                ) : "Log Masuk 🚀"}
              </Button>

              <div className="text-center text-sm space-y-2 pt-2">
                <button type="button" onClick={() => setMode("register-parent")} className="text-red-500 hover:text-red-600 font-bold hover:underline transition-colors">
                  Belum daftar? Daftar di sini! 🎉
                </button>
                <br />
                <button type="button" onClick={() => setMode("forgot-password")} className="text-gray-400 hover:text-gray-500 font-bold hover:underline transition-colors">
                  Lupa kata laluan? 🔑
                </button>
              </div>
            </form>
          )}

          {/* STAFF LOGIN */}
          {mode === "login" && userType === "staff" && (
            <form onSubmit={handleStaffLogin} className="space-y-4">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
                <TeacherIcon size="lg" /> Log Masuk Penyelia/Pendidik
              </h2>
              
              {/* Biometric Quick Login Button - Show if supported and enabled */}
              {biometricSupported && biometricEnabled && canUseBiometricLogin() && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    className="w-full py-4 bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white font-black text-lg rounded-2xl shadow-lg shadow-emerald-200/50 transition-all hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3"
                    data-testid="staff-biometric-login-btn"
                  >
                    {biometricLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        Mengesahkan...
                      </span>
                    ) : (
                      <>
                        <BiometricIcon className="w-6 h-6" />
                        Log Masuk {getBiometricName()}
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 font-bold">ATAU</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                </div>
              )}
              
              {/* Biometric hint - Show if supported but NOT enabled */}
              {biometricSupported && !biometricEnabled && (
                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <BiometricIcon className="w-5 h-5" />
                    <span className="text-sm font-bold">
                      {getBiometricName()} tersedia!
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Log masuk dahulu, kemudian aktifkan di Tetapan Profil untuk log masuk pantas.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                  <input
                    type="email"
                    data-testid="staff-email-input"
                    className="w-full pl-12 pr-4 py-4 border-3 border-blue-200 rounded-2xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={staffData.email}
                    onChange={(e) => setStaffData({...staffData, email: e.target.value})}
                    required
                    placeholder="email@contoh.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Kata Laluan</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                  <input
                    type="password"
                    data-testid="staff-password-input"
                    className="w-full pl-12 pr-4 py-4 border-3 border-blue-200 rounded-2xl focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all outline-none bg-white font-bold text-gray-700 placeholder-gray-400"
                    value={staffData.password}
                    onChange={(e) => setStaffData({...staffData, password: e.target.value})}
                    required
                    placeholder="Masukkan kata laluan"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="staff-login-submit"
                disabled={loading} 
                className="w-full py-4 bg-gradient-to-r from-blue-400 to-purple-400 hover:from-blue-500 hover:to-purple-500 text-white font-black text-lg rounded-2xl shadow-lg shadow-blue-200/50 transition-all hover:shadow-xl hover:-translate-y-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    Memproses...
                  </span>
                ) : "Log Masuk 🚀"}
              </Button>

              <div className="text-center text-sm space-y-2 pt-2">
                <button type="button" onClick={() => setMode("register-staff")} className="text-blue-500 hover:text-blue-600 font-bold hover:underline transition-colors flex items-center justify-center gap-1 mx-auto">
                  Daftar sebagai Penyelia/Pendidik <TeacherIcon size="sm" />
                </button>
                <br />
                <button type="button" onClick={() => setMode("forgot-password")} className="text-gray-400 hover:text-gray-500 font-bold hover:underline transition-colors">
                  Lupa kata laluan? 🔑
                </button>
              </div>
            </form>
          )}

          {/* PARENT REGISTRATION */}
          {mode === "register-parent" && (
            <form onSubmit={handleParentRegister} className="space-y-3">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4">
                👶 Pendaftaran Ibu Bapa
              </h2>
              
              <div className="bg-yellow-50 border-3 border-yellow-300 rounded-2xl p-3 text-sm text-yellow-700 font-bold">
                📱 Nombor telefon akan digunakan untuk log masuk
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nombor Telefon *</label>
                <PhoneValidation
                  value={parentData.phone}
                  onChange={(value) => setParentData({...parentData, phone: value})}
                  onValidationChange={setPhoneValidationPassed}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Anda *</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-3 border-orange-200 rounded-2xl focus:border-orange-400 outline-none font-bold"
                  value={parentData.name}
                  onChange={(e) => setParentData({...parentData, name: e.target.value})}
                  required
                  placeholder="Nama penuh"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama Pasangan</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border-3 border-pink-200 rounded-2xl focus:border-pink-400 outline-none font-bold"
                  value={parentData.spouse_name}
                  onChange={(e) => setParentData({...parentData, spouse_name: e.target.value})}
                  placeholder="Nama suami/isteri (pilihan)"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Cawangan *</label>
                <select
                  className="w-full px-4 py-3 border-3 border-green-200 rounded-2xl focus:border-green-400 outline-none appearance-none bg-white font-bold"
                  value={parentData.branch}
                  onChange={(e) => setParentData({...parentData, branch: e.target.value})}
                  required
                >
                  <option value="">-- Pilih Cawangan --</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.name}>{branch.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Kata Laluan *</label>
                <input
                  type="password"
                  className="w-full px-4 py-3 border-3 border-purple-200 rounded-2xl focus:border-purple-400 outline-none font-bold"
                  value={parentData.password}
                  onChange={(e) => setParentData({...parentData, password: e.target.value})}
                  required
                  minLength={6}
                  placeholder="Minimum 6 aksara"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full py-4 bg-gradient-to-r from-green-400 to-cyan-400 text-white font-black rounded-2xl shadow-lg mt-2"
              >
                {loading ? "Memproses..." : "Daftar Sekarang 🎉"}
              </Button>

              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-gray-500 font-bold hover:underline pt-2">
                ← Sudah ada akaun? Log masuk
              </button>
            </form>
          )}

          {/* STAFF REGISTRATION */}
          {mode === "register-staff" && (
            <form onSubmit={handleStaffRegister} className="space-y-3">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
                <TeacherIcon size="lg" /> Pendaftaran Penyelia/Pendidik
              </h2>
              
              <div className="bg-blue-50 border-3 border-blue-300 rounded-2xl p-3 text-sm text-blue-700 font-bold">
                ⚠️ Pendaftaran memerlukan kelulusan dari Superadmin
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nama *</label>
                <input type="text" className="w-full px-4 py-3 border-3 border-blue-200 rounded-2xl focus:border-blue-400 outline-none font-bold" value={staffData.name} onChange={(e) => setStaffData({...staffData, name: e.target.value})} required placeholder="Nama penuh" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Email *</label>
                <input type="email" className="w-full px-4 py-3 border-3 border-purple-200 rounded-2xl focus:border-purple-400 outline-none font-bold" value={staffData.email} onChange={(e) => setStaffData({...staffData, email: e.target.value})} required placeholder="email@contoh.com" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Peranan *</label>
                <select className="w-full px-4 py-3 border-3 border-green-200 rounded-2xl focus:border-green-400 outline-none bg-white font-bold" value={staffData.role} onChange={(e) => setStaffData({...staffData, role: e.target.value})}>
                  <option value="guru">Guru</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Kata Laluan *</label>
                <input type="password" className="w-full px-4 py-3 border-3 border-orange-200 rounded-2xl focus:border-orange-400 outline-none font-bold" value={staffData.password} onChange={(e) => setStaffData({...staffData, password: e.target.value})} required minLength={6} placeholder="Minimum 6 aksara" />
              </div>

              <Button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-blue-400 to-purple-400 text-white font-black rounded-2xl shadow-lg mt-2">
                {loading ? "Memproses..." : "Hantar Permohonan 📨"}
              </Button>

              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-gray-500 font-bold hover:underline pt-2">
                ← Kembali ke log masuk
              </button>
            </form>
          )}

          {/* FORGOT PASSWORD */}
          {mode === "forgot-password" && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <h2 className="text-xl font-black text-gray-800 text-center mb-4">
                🔑 Lupa Kata Laluan
              </h2>
              
              <div className="bg-cyan-50 border-3 border-cyan-300 rounded-2xl p-3 text-sm text-cyan-700 font-bold">
                📧 Link reset password akan dihantar ke email anda
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
                  <input
                    type="email"
                    className="w-full pl-12 pr-4 py-4 border-3 border-cyan-200 rounded-2xl focus:border-cyan-400 outline-none font-bold"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    placeholder="Masukkan email anda"
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-cyan-400 to-blue-400 text-white font-black rounded-2xl shadow-lg">
                {loading ? "Menghantar..." : "Hantar Link Reset 📩"}
              </Button>

              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-gray-500 font-bold hover:underline pt-2">
                ← Kembali ke log masuk
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes floatDecor {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(10deg); }
          66% { transform: translateY(-10px) rotate(-5deg); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
